import { useMemo, useRef } from "react";
import type { Area } from "react-easy-crop";

import { EditorToolbar } from "../components/editor/EditorToolbar";
import { WorkspaceStage } from "../components/editor/WorkspaceStage";
import { AppShell } from "../components/layout/AppShell";
import { SectionContainer } from "../components/layout/SectionContainer";
import { TopHeader } from "../components/layout/TopHeader";
import { PredictPanel } from "../components/prediction/PredictPanel";
import { PredictionResultCard } from "../components/prediction/PredictionResultCard";
import { ModelSummaryPanel } from "../components/stats/ModelSummaryPanel";
import { ImageSessionInfo } from "../components/upload/ImageSessionInfo";
import { UploadDropzone } from "../components/upload/UploadDropzone";
import {
  buildExportFileName,
  downloadBlob,
  hasMeaningfulEdits,
  readImageDimensions,
  renderEditedImage
} from "../lib/image-processing";
import { useModelSummary } from "../hooks/useModelSummary";
import { useRenderedImage } from "../hooks/useRenderedImage";
import { predictImage } from "../services/api";
import { useHorizonStore } from "../store/useHorizonStore";

const MAX_CLIENT_UPLOAD_MB = 10;

export function App() {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const originalImage = useHorizonStore((state) => state.originalImage);
  const renderedImage = useHorizonStore((state) => state.renderedImage);
  const editorState = useHorizonStore((state) => state.editorState);
  const renderStatus = useHorizonStore((state) => state.renderStatus);
  const renderError = useHorizonStore((state) => state.renderError);
  const modelSummary = useHorizonStore((state) => state.modelSummary);
  const modelSummaryStatus = useHorizonStore((state) => state.modelSummaryStatus);
  const modelSummaryError = useHorizonStore((state) => state.modelSummaryError);
  const predictionStatus = useHorizonStore((state) => state.predictionStatus);
  const predictionMessage = useHorizonStore((state) => state.predictionMessage);
  const predictionError = useHorizonStore((state) => state.predictionError);
  const predictionResult = useHorizonStore((state) => state.predictionResult);
  const predictionPreviewUrl = useHorizonStore((state) => state.predictionPreviewUrl);
  const setOriginalImage = useHorizonStore((state) => state.setOriginalImage);
  const clearSession = useHorizonStore((state) => state.clearSession);
  const setCropPosition = useHorizonStore((state) => state.setCropPosition);
  const setCropAreaPixels = useHorizonStore((state) => state.setCropAreaPixels);
  const setZoom = useHorizonStore((state) => state.setZoom);
  const setRotation = useHorizonStore((state) => state.setRotation);
  const setFlipHorizontal = useHorizonStore((state) => state.setFlipHorizontal);
  const setFlipVertical = useHorizonStore((state) => state.setFlipVertical);
  const setBrightness = useHorizonStore((state) => state.setBrightness);
  const setContrast = useHorizonStore((state) => state.setContrast);
  const setSaturation = useHorizonStore((state) => state.setSaturation);
  const setBlur = useHorizonStore((state) => state.setBlur);
  const setSharpen = useHorizonStore((state) => state.setSharpen);
  const setAspectPreset = useHorizonStore((state) => state.setAspectPreset);
  const toggleCompareMode = useHorizonStore((state) => state.toggleCompareMode);
  const resetEdits = useHorizonStore((state) => state.resetEdits);
  const setPredictionLoading = useHorizonStore((state) => state.setPredictionLoading);
  const setPredictionSuccess = useHorizonStore((state) => state.setPredictionSuccess);
  const setPredictionError = useHorizonStore((state) => state.setPredictionError);
  const resetPrediction = useHorizonStore((state) => state.resetPrediction);

  useRenderedImage();
  const { refreshSummary } = useModelSummary();

  const runtimeState = modelSummary?.runtime.warmup_state ?? "idle";
  const activePredictionSource = hasMeaningfulEdits(editorState) ? "edited" : "original";
  const activeInferencePreviewUrl = useMemo(() => {
    if (!originalImage) {
      return null;
    }
    return activePredictionSource === "edited" ? renderedImage?.objectUrl ?? originalImage.objectUrl : originalImage.objectUrl;
  }, [activePredictionSource, originalImage, renderedImage]);
  const predictDisabled = !originalImage || predictionStatus === "loading" || runtimeState === "error";

  const handleUpload = async (file: File) => {
    if (!file.type.startsWith("image/")) {
      setPredictionError("Please choose a valid image file.");
      return;
    }

    if (file.size > MAX_CLIENT_UPLOAD_MB * 1024 * 1024) {
      setPredictionError(`Please choose an image smaller than ${MAX_CLIENT_UPLOAD_MB} MB.`);
      return;
    }

    const dimensions = await readImageDimensions(file);
    const objectUrl = URL.createObjectURL(file);
    setOriginalImage({
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      width: dimensions.width,
      height: dimensions.height,
      objectUrl
    });
    resetPrediction();
  };

  const buildPredictionCandidate = async () => {
    if (!originalImage) {
      throw new Error("No active image.");
    }

    if (activePredictionSource === "edited") {
      return {
        image: renderedImage ?? (await renderEditedImage(originalImage.objectUrl, editorState)),
        source: "edited" as const
      };
    }

    return {
      image: { blob: originalImage.file, objectUrl: originalImage.objectUrl, updatedAt: new Date().toISOString() },
      source: "original" as const
    };
  };

  const handleExport = async () => {
    if (!originalImage) {
      return;
    }
    const candidate = await buildPredictionCandidate();
    downloadBlob(candidate.image.blob, buildExportFileName(originalImage.name));
  };

  const handlePredict = async () => {
    if (!originalImage) {
      return;
    }

    const waitingForModel = runtimeState === "idle" || runtimeState === "loading";
    setPredictionLoading(waitingForModel ? "Waiting for model..." : "Running inference...");

    try {
      const candidate = await buildPredictionCandidate();
      const result = await predictImage({
        image: candidate.image.blob,
        filename: candidate.source === "edited" ? buildExportFileName(originalImage.name) : originalImage.name,
        source: candidate.source
      });
      setPredictionSuccess(result, candidate.image.objectUrl, candidate.source);
      await refreshSummary();
    } catch (error) {
      setPredictionError(error instanceof Error ? error.message : "Prediction failed.");
      await refreshSummary();
    }
  };

  return (
    <AppShell>
      <TopHeader summary={modelSummary} />

      <main className="grid gap-8 xl:grid-cols-[minmax(0,1.45fr)_420px]">
        <SectionContainer className="space-y-5" eyebrow="Workspace" title="Upload and refine the active image">
          {originalImage ? (
            <>
              <WorkspaceStage
                editorState={editorState}
                image={originalImage}
                renderError={renderError}
                renderStatus={renderStatus}
                renderedImage={renderedImage}
                onCropChange={setCropPosition}
                onCropComplete={(_: Area, croppedAreaPixels: Area) => {
                  setCropAreaPixels({
                    x: croppedAreaPixels.x,
                    y: croppedAreaPixels.y,
                    width: croppedAreaPixels.width,
                    height: croppedAreaPixels.height
                  });
                }}
                onZoomChange={setZoom}
              />
              <ImageSessionInfo
                image={originalImage}
                onClear={clearSession}
                onReplace={() => fileInputRef.current?.click()}
                onResetEdits={resetEdits}
              />
            </>
          ) : (
            <UploadDropzone onSelectFile={handleUpload} />
          )}
          <input
            ref={fileInputRef}
            accept="image/*"
            className="hidden"
            type="file"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) {
                void handleUpload(file);
              }
              event.target.value = "";
            }}
          />
        </SectionContainer>

        <SectionContainer eyebrow="Controls" title="Editing and prediction">
          {originalImage ? (
            <>
              <EditorToolbar
                editorState={editorState}
                renderStatus={renderStatus}
                onAspectChange={setAspectPreset}
                onExport={() => {
                  void handleExport();
                }}
                onFlipHorizontal={setFlipHorizontal}
                onFlipVertical={setFlipVertical}
                onResetEdits={resetEdits}
                onRotateLeft={() => setRotation(editorState.rotation - 90)}
                onRotateRight={() => setRotation(editorState.rotation + 90)}
                onSetBlur={setBlur}
                onSetBrightness={setBrightness}
                onSetContrast={setContrast}
                onSetSaturation={setSaturation}
                onSetSharpen={setSharpen}
                onSetZoom={setZoom}
                onToggleCompare={toggleCompareMode}
              />

              <PredictPanel
                activePreviewUrl={activeInferencePreviewUrl}
                activeSource={activePredictionSource}
                disabled={predictDisabled}
                isLoading={predictionStatus === "loading"}
                message={predictionMessage}
                modelSummary={modelSummary}
                predictionError={predictionError}
                onPredict={() => {
                  void handlePredict();
                }}
              />
            </>
          ) : (
            <div className="rounded-[24px] border border-dashed border-white/12 bg-white/[0.02] px-6 py-10 text-center text-sm text-ink-soft">
              Load an image first to unlock the browser-side editor and prediction controls.
            </div>
          )}
        </SectionContainer>
      </main>

      <PredictionResultCard prediction={predictionResult} previewUrl={predictionPreviewUrl} />
      <ModelSummaryPanel error={modelSummaryError} status={modelSummaryStatus} summary={modelSummary} />
    </AppShell>
  );
}
